import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiEye, FiEyeOff, FiLoader, FiAlertCircle, FiShield, FiUser, FiCheckCircle, FiEdit, FiPlus, FiBookOpen, FiPhone, FiMapPin } = FiIcons;

const UserForm = ({ user, onClose, onSave }) => {
  const { user: currentUser, createUser, updateUser, hasPermission } = useAuth();
  const { subjects: allSubjects } = useData();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const isEditingSelf = currentUser?.uid === (user?.uid || user?.id);
  const canManageFull = currentUser?.role === 'admin' || hasPermission('canManageUsers');

  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: user?.address || '',
    password: '',
    role: user?.role || 'tutor',
    status: user?.status || 'active',
    assignedSubjects: user?.assignedSubjects || [],
    permissions: user?.permissions || {
      canManageStudents: false,
      canManageUsers: false,
      canViewReports: false,
      canManageSessions: false,
      canManageParents: false,
      canManageSubjects: false
    }
  });

  const roles = [
    { id: 'admin', label: 'Administrator', desc: 'Full system access' },
    { id: 'tutor', label: 'Tutor', desc: 'Manage assigned students' },
    { id: 'parent', label: 'Parent', desc: 'Access to child portal' },
    { id: 'viewer', label: 'Viewer', desc: 'Read-only access' }
  ];

  const handlePermissionChange = (perm) => {
    setFormData(prev => ({
      ...prev,
      permissions: { ...prev.permissions, [perm]: !prev.permissions[perm] }
    }));
  };

  const toggleSubject = (sub) => {
    setFormData(prev => {
      const exists = prev.assignedSubjects.includes(sub);
      const updated = exists 
        ? prev.assignedSubjects.filter(s => s !== sub) 
        : [...prev.assignedSubjects, sub];
      return { ...prev, assignedSubjects: updated };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      let result;
      if (user) {
        result = await updateUser(user.uid || user.id, formData);
      } else {
        if (!formData.password) throw new Error('Password is required');
        result = await createUser(formData);
      }

      if (result.success) onSave();
      else setError(result.message);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }} 
        animate={{ opacity: 1, scale: 1 }} 
        className="bg-white rounded-[2.5rem] shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between p-8 border-b border-gray-100">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-600">
              <SafeIcon icon={user ? FiEdit : FiPlus} className="text-2xl" />
            </div>
            <div>
              <h2 className="text-2xl font-black text-gray-900 tracking-tight">
                {user ? 'Edit User' : 'Add New User'}
              </h2>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">{user?.email || 'New Account'}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-4 hover:bg-gray-100 rounded-2xl text-gray-400 hover:text-gray-600 transition-all">
            <SafeIcon icon={FiX} className="text-xl" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-10">
          {error && (
            <div className="bg-rose-50 text-rose-700 p-5 rounded-2xl text-sm font-bold border border-rose-100 flex items-center gap-3">
              <SafeIcon icon={FiAlertCircle} className="text-xl flex-shrink-0" />
              <div className="flex-1">{error}</div>
            </div>
          )}

          <section className="space-y-6">
            <h3 className="text-xs font-black text-indigo-600 uppercase tracking-[0.2em] flex items-center gap-2">
              <div className="w-1.5 h-1.5 bg-indigo-600 rounded-full"></div>
              Identity & Contact
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <input 
                type="text" 
                value={formData.firstName} 
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                className="w-full p-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-indigo-600 outline-none font-bold" 
                placeholder="First Name" 
                required 
              />
              <input 
                type="text" 
                value={formData.lastName} 
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                className="w-full p-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-indigo-600 outline-none font-bold" 
                placeholder="Last Name" 
                required 
              />
              <input 
                type="email" 
                value={formData.email} 
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full p-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-indigo-600 outline-none font-bold" 
                placeholder="Email Address" 
                required 
                disabled={!!user}
              />
              <div className="relative">
                <SafeIcon icon={FiPhone} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input 
                  type="tel" 
                  value={formData.phone} 
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-indigo-600 outline-none font-bold" 
                  placeholder="Phone Number" 
                />
              </div>
              <div className="relative md:col-span-2">
                <SafeIcon icon={FiMapPin} className="absolute left-4 top-5 text-gray-400" />
                <textarea 
                  value={formData.address} 
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-indigo-600 outline-none font-bold min-h-[100px]" 
                  placeholder="Physical Address" 
                />
              </div>
              
              {!user && (
                <div className="relative md:col-span-2">
                   <SafeIcon icon={FiShield} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input 
                    type={showPassword ? "text" : "password"} 
                    value={formData.password} 
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full pl-12 pr-12 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-indigo-600 outline-none font-bold" 
                    placeholder="Initial Password" 
                    required 
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
                    <SafeIcon icon={showPassword ? FiEyeOff : FiEye} />
                  </button>
                </div>
              )}
            </div>
          </section>

          {(canManageFull && !isEditingSelf) && (
            <section className="space-y-6">
              <h3 className="text-xs font-black text-indigo-600 uppercase tracking-[0.2em] flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-indigo-600 rounded-full"></div>
                Access & Governance
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  {roles.map(r => (
                    <button 
                      key={r.id} 
                      type="button" 
                      onClick={() => setFormData({ ...formData, role: r.id })}
                      className={`w-full p-4 rounded-2xl border-2 text-left transition-all ${formData.role === r.id ? 'border-indigo-600 bg-indigo-50 shadow-lg' : 'border-gray-100 hover:border-gray-200 bg-white'}`}
                    >
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-black text-gray-900">{r.label}</span>
                        {formData.role === r.id && <SafeIcon icon={FiCheckCircle} className="text-indigo-600" />}
                      </div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase">{r.desc}</p>
                    </button>
                  ))}
                </div>
                <div className="bg-gray-50 p-6 rounded-[2rem] space-y-3">
                  {Object.keys(formData.permissions).map(perm => (
                    <label key={perm} className="flex items-center justify-between p-3 bg-white rounded-xl border border-gray-100 cursor-pointer">
                      <span className="text-xs font-bold text-gray-700">{perm.replace(/([A-Z])/g, ' $1').trim()}</span>
                      <input 
                        type="checkbox" 
                        checked={formData.permissions[perm]} 
                        onChange={() => handlePermissionChange(perm)}
                        className="w-5 h-5 rounded-lg text-indigo-600" 
                      />
                    </label>
                  ))}
                </div>
              </div>
            </section>
          )}

          {formData.role === 'tutor' && (
            <section className="space-y-6">
              <h3 className="text-xs font-black text-indigo-600 uppercase tracking-[0.2em] flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-indigo-600 rounded-full"></div>
                Specialization
              </h3>
              <div className="bg-gray-50 p-6 rounded-[2rem] grid grid-cols-2 md:grid-cols-3 gap-3">
                {allSubjects.map(sub => (
                  <button 
                    key={sub} 
                    type="button" 
                    onClick={() => toggleSubject(sub)}
                    className={`flex items-center gap-2 px-4 py-3 rounded-xl border-2 transition-all font-bold text-xs ${formData.assignedSubjects.includes(sub) ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-white border-gray-100 text-gray-400'}`}
                  >
                    <SafeIcon icon={FiBookOpen} className="text-sm" />
                    {sub}
                  </button>
                ))}
              </div>
            </section>
          )}

          <div className="flex justify-end gap-4 pt-10 border-t border-gray-100">
            <button type="button" onClick={onClose} className="px-8 py-4 text-gray-400 font-black text-xs uppercase hover:text-gray-600">Cancel</button>
            <button 
              type="submit" 
              disabled={loading} 
              className="px-12 py-4 bg-indigo-600 text-white rounded-2xl shadow-xl font-black text-xs uppercase hover:bg-indigo-700 disabled:opacity-50 min-w-[200px]"
            >
              {loading ? <SafeIcon icon={FiLoader} className="animate-spin text-lg mx-auto" /> : (user ? 'Update Profile' : 'Create Account')}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default UserForm;